import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { isExpired, decodeToken } from 'react-jwt';

export const getApiHost = (): string => {
  const hostname = window.location.hostname;

  if (hostname.includes('develop')) {
    return 'https://sbs-graphql-develop.paytrust2.com';
  } else if (hostname.includes('stage')) {
    return 'https://sbs-graphql-stage.paytrust2.com';
  } else if (hostname.includes('master')) {
    return 'https://sbs-graphql-master.paytrust2.com';
  } else if (hostname.includes('localhost')) {
    return 'http://localhost:3000';
  } else if (hostname.includes('app.silverbills.com')) {
    return 'https://sbs-graphql.silverbills.com';
  } else {
    throw new Error(`Unrecognized hostname: ${hostname}`);
  }
};

const apiHost = getApiHost();
const getGraphQLUri = () => `${apiHost}/graphql`;

const httpLink = new HttpLink({
  uri: getGraphQLUri(), // Dynamically set the GraphQL endpoint
});

const REFRESH_LOGIN_QUERY = `
  query RefreshLogin($username: String!, $refreshToken: String!) {
    refreshLogin(username: $username, refreshToken: $refreshToken) {
      accessToken
      refreshToken
    }
  }
`;

const refreshAccessToken = async (): Promise<string | null> => {
  const currentToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');

  if (!currentToken || !refreshToken || !isExpired(currentToken)) {
    return currentToken;
  }

  let username: string | undefined;
  try {
    const decoded: any = decodeToken(currentToken);
    username = decoded?.username;
  } catch (e) {
    console.error('Failed to decode token', e);
  }

  if (!username) return currentToken;

  try {
    const response = await fetch(getGraphQLUri(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: REFRESH_LOGIN_QUERY,
        variables: { username, refreshToken },
      }),
    });
    const result = await response.json();
    const tokens = result?.data?.refreshLogin;
    if (tokens?.accessToken && tokens?.refreshToken) {
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      return tokens.accessToken as string;
    }
  } catch (e) {
    console.error('Failed to refresh token', e);
  }

  localStorage.clear();
  return null;
};

const authLink = setContext(async (_, { headers }) => {
  let token = localStorage.getItem('accessToken');
  if (token && isExpired(token)) {
    token = await refreshAccessToken();
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

export default client;
